/**
 * Individual cell component in the grid
 * Contains canvas and overlay elements
 */

import type { SimulationState, CellPosition, SimulationStatus } from '../types/simulation';
import { cellIdFromPosition } from '../types/simulation';

export interface CellEvents {
  onClick?: (position: CellPosition) => void;
  onInitialize?: (position: CellPosition, canvas: HTMLCanvasElement) => void;
}

export class Cell {
  readonly position: CellPosition;
  readonly id: string;

  private container: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private overlay: HTMLDivElement;
  private idLabel: HTMLDivElement;
  private statusLabel: HTMLDivElement;
  private metricsDiv: HTMLDivElement;
  private scenarioLabel: HTMLDivElement;

  private _initialized: boolean = false;
  private events: CellEvents;
  private resizeObserver: ResizeObserver | null = null;

  constructor(position: CellPosition, events: CellEvents = {}) {
    this.position = position;
    this.id = cellIdFromPosition(position);
    this.events = events;

    // Create container
    this.container = document.createElement('div');
    this.container.className = 'cell empty';
    this.container.dataset.row = String(position.row);
    this.container.dataset.col = String(position.col);
    this.container.dataset.cellId = this.id;

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.container.appendChild(this.canvas);

    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'cell-overlay';
    this.container.appendChild(this.overlay);

    // Create header (ID and status)
    const header = document.createElement('div');
    header.className = 'cell-header';
    this.overlay.appendChild(header);

    this.idLabel = document.createElement('div');
    this.idLabel.className = 'cell-id';
    this.idLabel.textContent = `${position.row + 1}-${position.col + 1}`;
    header.appendChild(this.idLabel);

    this.statusLabel = document.createElement('div');
    this.statusLabel.className = 'cell-status';
    this.statusLabel.textContent = 'empty';
    header.appendChild(this.statusLabel);

    // Create metrics panel
    this.metricsDiv = document.createElement('div');
    this.metricsDiv.className = 'cell-metrics';
    this.metricsDiv.style.display = 'none';
    this.overlay.appendChild(this.metricsDiv);

    // Create scenario label
    this.scenarioLabel = document.createElement('div');
    this.scenarioLabel.className = 'cell-scenario';
    this.scenarioLabel.style.display = 'none';
    this.overlay.appendChild(this.scenarioLabel);

    // Click handler
    this.container.addEventListener('click', this.handleClick);

    // Resize observer for canvas
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.container);
  }

  get element(): HTMLDivElement {
    return this.container;
  }

  get isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Set active state
   */
  setActive(active: boolean): void {
    this.container.classList.toggle('active', active);
  }

  /**
   * Update display from simulation state
   */
  updateState(state: SimulationState): void {
    // Update status
    this.setStatus(state.status);

    // Update metrics if initialized
    if (state.status !== 'empty' && state.status !== 'loading') {
      this.metricsDiv.style.display = 'block';
      this.metricsDiv.innerHTML = `
        <div class="metric"><span class="metric-label">FPS:</span> <span class="metric-value">${state.fps}</span></div>
        <div class="metric"><span class="metric-label">Entities:</span> <span class="metric-value">${state.entityCount}</span></div>
        <div class="metric"><span class="metric-label">Tick:</span> <span class="metric-value">${state.tick}</span></div>
      `;
    } else {
      this.metricsDiv.style.display = 'none';
    }

    // Update scenario name
    if (state.scenarioName) {
      this.scenarioLabel.style.display = 'block';
      this.scenarioLabel.textContent = state.scenarioName;
    } else {
      this.scenarioLabel.style.display = 'none';
    }
  }

  /**
   * Mark as initialized
   */
  markInitialized(): void {
    this._initialized = true;
    this.container.classList.remove('empty');
  }

  /**
   * Get canvas size
   */
  getSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
  }

  /**
   * Clean up
   */
  dispose(): void {
    this.container.removeEventListener('click', this.handleClick);
    this.resizeObserver?.disconnect();
    this.container.remove();
  }

  private setStatus(status: SimulationStatus): void {
    this.statusLabel.textContent = status;
    this.statusLabel.className = 'cell-status';

    // Remove old status classes
    this.container.classList.remove('empty', 'loading', 'error');

    switch (status) {
      case 'running':
        this.statusLabel.classList.add('running');
        break;
      case 'paused':
        this.statusLabel.classList.add('paused');
        break;
      case 'error':
        this.statusLabel.classList.add('error');
        this.container.classList.add('error');
        break;
      case 'loading':
        this.container.classList.add('loading');
        break;
      case 'empty':
        this.container.classList.add('empty');
        break;
    }
  }

  private handleClick = (): void => {
    if (!this._initialized) {
      // First click initializes the cell
      this._initialized = true;
      this.container.classList.remove('empty');
      this.container.classList.add('loading');
      this.statusLabel.textContent = 'loading';
      this.events.onInitialize?.(this.position, this.canvas);
    }
    this.events.onClick?.(this.position);
  };

  private handleResize = (entries: ResizeObserverEntry[]): void => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) {
        this.canvas.width = width * window.devicePixelRatio;
        this.canvas.height = height * window.devicePixelRatio;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
      }
    }
  };
}
